import styled from 'styled-components';
import { ErrorBanner, WarningBanner, InfoBanner } from 'ui/banner';
import { NewTabLink } from 'ui/links';
import { InputText } from 'ui/inputs/inputText';
import { TextArea } from 'ui/inputs/textArea';
import { SecondaryButton } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { Spinner } from 'ui/spinner';
import { Divider } from 'ui/divider';
import HoverCard from 'ui/card/HoverCard';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { TableHeaderCell } from 'ui/tableHeaderCell';
import { Table } from 'ui/table';
import { LinkButton } from 'ui/linkButton';
import { Menu } from 'ui/menu/Menu';
import { Skeleton } from 'ui/skeleton/Skeleton';
import {
  body1,
  body2,
  borderRadius,
  caption,
  colors,
  duration,
  easing,
  fonts,
  fontSizes,
  fontWeights,
  heading4,
  heading5,
  largeShadow,
  Monospace2,
  smallShadow,
  spacing,
  subtitle1,
  subtitle2,
  subtleShadow,
} from 'ui/theme';
import { Body2, Caption, H4, Subtitle2, body2Bold, heading3 } from 'ui/theme/typography';
import { Logo } from 'ui/logo/Logo';
import { ArrowRightCircle } from 'ui/icons/navigation/ArrowRightCircle';
import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { Edit16 } from 'ui/icons/action/Edit16';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { pxToRem } from 'utils/convert';
import { EventConditionArrowSvg } from '../assets/EventConditionArrow';
import { LimitExceeded } from '../assets/LimitExceeded';

/** Common
 Used by more than page, or by a common component
 */

export const MarginLessBody2 = styled(Body2)`
  margin: 0;
`;

const appWidthConstraint = `
  margin-left: -${pxToRem(40)};
  margin-right: -${pxToRem(40)};
`;

const bodyMarginOverride = `
  margin-bottom: ${spacing.quarter};
`;

const bannerMarginOverride = `
  margin-bottom: ${spacing.half};
`;

export const StyledFullWidth = styled.div`
  ${appWidthConstraint};
`;

export const MarginlessBoldSubtitle2 = styled(Subtitle2)`
  font-weight: ${fontWeights.semiBold};
  margin: 0;
`;
export const BottomMarginBoldSubtitle2 = styled(MarginlessBoldSubtitle2)`
  margin: 0 0 ${spacing.quarter} 0;
`;

export const Body2Bold = styled(Body2)`
  font-weight: ${fontWeights.bold};
`;

export const InfoText = styled(Body2)`
  color: ${colors.secondaryText};
  margin-bottom: 0;
`;

export const EmptyDescription = styled.div`
  ${body1};
  color: ${colors.textDisabled};
`;

export const PrevStrengthText = styled(Caption)`
  color: ${colors.grey50};
`;

export const PrevStrengthBoldText = styled(PrevStrengthText)`
  font-weight: ${fontWeights.semiBold};
`;

export const InfoWrapper = styled.div`
  display: flex;
`;

export const InfoHeader = styled.h4`
  ${heading4};
  margin: 0;
  padding-bottom: ${spacing.base};
`;

export const MainLayoutWrapper = styled(StyledFullWidth)`
  display: flex;
  justify-content: center;

  &[data-background-color='${colors.backgroundSecondary}'] {
    background: ${colors.backgroundSecondary};
  }
  &[data-background-color='${colors.white}'] {
    background: ${colors.white};
  }
`;

export const MainLayoutHeaderWrapper = styled(MainLayoutWrapper)`
  margin-top: -${pxToRem(20)};
`;

export const CreatedAndEditGoalCriteriaWrapper = styled(StyledFullWidth)`
  margin-top: ${spacing.x3};
  margin-bottom: ${spacing.x10};
`;

export const MainContentLayout = styled.div`
  width: 100%;
  max-width: ${pxToRem(1216)};
`;

export const LayoutWithPanel = styled.div`
  display: flex;
  width: 100%;
  max-width: ${pxToRem(1216)};
  margin: ${spacing.x3} 0;
`;

export const DetailsSectionWrapper = styled.div`
  background-color: ${colors.grey1};
  border-radius: ${spacing.half};
  padding: ${spacing.plusHalf};
`;

export const DetailsInputRow = styled.div`
  display: flex;
  margin-top: ${spacing.plusHalf};
`;

export const InputLabel = styled.div`
  ${subtitle2};
  width: ${spacing.x16};
  white-space: none;
  display: flex;
  gap: ${spacing.quarter};
  align-items: center;
  align-self: start;
`;

export const EmptyStateContainer = styled.div`
  background: ${colors.white};
  text-align: center;
  padding: ${spacing.x4} 0;
  border-radius: ${spacing.half};
  ${smallShadow};
`;

export const Title = styled.div`
  ${heading4};
`;

export const EmptyStateSubtitle = styled.div`
  ${body1};
  color: ${colors.textSecondary};
`;

export const StyledInputText = styled(InputText)`
  width: ${pxToRem(416)};
`;

export const StyledTextArea = styled(TextArea)`
  width: ${pxToRem(416)};
`;

export const DescriptionContainer = styled(InputLabel)`
  flex-direction: column;
  align-items: flex-start;
`;

export const StyledQuerySubText = styled(Body2)`
  color: ${colors.textSecondary};
`;

export const MenuOptionEnabled = styled.p`
  ${body2};
  align-items: center;
  display: inline-flex;
  width: 100%;
  padding: ${spacing.half};

  &[data-destructive] {
    color: ${colors.iconDestructive};
  }
`;
export const MenuOptionDisabled = styled.p`
  ${body2};
  align-items: center;
  background-color: ${colors.white};
  color: ${colors.grey25};
  cursor: not-allowed;
  display: inline-flex;
  width: 100%;
  padding: ${spacing.half};
`;

export const PaddedBaseMenu = styled(Menu)`
  & > div {
    padding: ${spacing.threeEights};
  }
`;

export const ModalText = styled.div`
  margin-bottom: ${spacing.base};

  :last-of-type {
    margin-bottom: 0;
  }
`;

export const monospaceSpacing = `
  padding: ${spacing.threeEights} ${spacing.quarter};
  margin-left: ${spacing.quarter};
`;

export const StyledMonospace = styled(Monospace2)`
  color: inherit;
  background-color: ${colors.grey5};
  ${monospaceSpacing};
  word-break: break-word;
  display: inline-block;
`;

export const StyledMonospaceForCell = styled(Monospace2)`
  background-color: inherit;
  color: inherit;
`;

export const DetailsWrapper = styled.div`
  width: 30%;
  color: ${colors.grey60};
  margin-right: ${spacing.double};
`;

export const DetailSection = styled.div`
  margin-bottom: ${spacing.plusHalf};
`;

export const ShortenedDivider = styled(Divider)`
  position: relative;
`;

export const EdgedDivider = styled(Divider)`
  margin: ${spacing.threeQuarter} -${spacing.threeQuarter};
`;

export const DetailHeaderWithMoreInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.eighth};
`;

export const TableHeaderWithMoreInfo = styled.div`
  display: table-cell;
  align-items: center;
  cursor: pointer;
`;

export const MoreInfoHeaderCell = styled.div`
  display: flex;
  align-items: center;
`;

export const CenterAlignedText = styled.div`
  text-align: center;
`;

export const GoalDetailCriteriaSection = styled.div`
  ${smallShadow};
  border-radius: ${spacing.half} ${spacing.half};
  padding: ${spacing.plusHalf} ${spacing.plusHalf} ${spacing.threeQuarter};
  background-color: ${colors.white};
`;

export const GoalDetailCardSection = styled.div`
  display: grid;
  grid-template-columns: ${spacing.double} 1fr;
  padding: ${spacing.double};
  column-gap: ${spacing.threeQuarter};
  ${smallShadow};
  border-radius: ${spacing.half};
  background-color: ${colors.white};
  margin-top: ${spacing.x3};
`;

export const GoalDetailInnerCard = styled.div`
  margin-top: ${spacing.base};
  padding: ${spacing.double};
  background-color: ${colors.grey1};
  border-radius: ${spacing.threeQuarter};
  width: ${spacing.x48};
`;

export const GoalCriteriaLeadText = styled.p`
  ${body1};
  margin: 0;
`;

export const GoalCriteriaContainer = styled.div`
  white-space: pre;
  flex-wrap: wrap;
  margin-bottom: ${spacing.threeQuarter};
  & > p {
    margin: 0;
  }

  & code {
    margin: 0 ${spacing.quarter} 0 ${spacing.quarter};
    word-break: break-word;
    display: inline-block;
  }

  & code:first-of-type {
    margin: 0 ${spacing.eighth} 0 ${spacing.quarter};
  }

  & code:last-of-type {
    margin: 0 ${spacing.quarter} 0 ${spacing.eighth};
  }
`;

export const GoalCriteriaHeader = styled(H4)`
  margin-top: ${spacing.x3};
  margin-bottom: ${spacing.threeQuarter};
`;

export const UpdatedGoalCriteriaHeader = styled(H4)`
  margin-bottom: 0;
`;

export const UpdatedGoalCriteriaHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.threeEights};
  margin-bottom: ${spacing.threeQuarter};
`;

export const PredictionGoalCriteriaWrapper = styled.div`
  margin-top: ${spacing.x3};
`;

export const GroupBorder = `
  border-left: 3px solid ${colors.grey5};
  padding-left: ${spacing.plusHalf};
  border-radius: ${spacing.single};
`;

export const GoalCriteriaInlineText = styled.span`
  ${subtitle2};
  color: ${colors.textPrimary};
`;

export const GoalCriteriaNestedCondition = styled.span`
  padding-top: ${spacing.plusHalf};
`;

export const GoalCriteriaNextLineGroup = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: ${spacing.threeQuarter};

  & code:first-of-type {
    margin: 0 ${spacing.eighth} 0 ${spacing.quarter};
  }

  & code:last-of-type {
    margin: 0 ${spacing.quarter} 0 ${spacing.eighth};
  }
  ${GroupBorder};

  &[data-condition='event'] {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const GoalCriteriaConditionIcon = styled(EventConditionArrowSvg)`
  transform: translateY(${spacing.eighth});
  margin: 0 ${spacing.half} 0 ${spacing.x7};

  &[data-is-nested-join='true'] {
    margin: 0 ${spacing.half} 0 ${spacing.x10};
  }
`;

export const GoalCriteriaNextLineBody = styled.span`
  ${body1};
`;

export const GoalCriteriaInlineCode = styled(StyledMonospace)`
  display: inline;
`;

export const GoalCriteriaVerticalSpace = styled.div`
  margin: ${spacing.double} 0 ${spacing.double} 0;
`;

export const ConditionSpacing = styled.div`
  ${bodyMarginOverride};
`;

export const GoalCriteriaTopClauseContainer = styled.div`
  margin-bottom: 0;
  &[data-is-event='true'] {
    margin-bottom: ${spacing.threeQuarter};
  }
`;

export const CreateOrEditActionLayout = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: ${spacing.base} ${spacing.plusHalf};
  font-size: ${fontSizes.s12};
  gap: ${spacing.half};
`;

export const CreateOrEditActionFooter = styled.div`
  position: fixed;
  bottom: 0;
  background: white;
  z-index: 1;
  width: 100%;
  border-top: ${spacing.single} solid ${colors.grey15};
  left: 0;
`;

export const GoalHeaderLayout = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const AlignedPageHeader = styled(PageHeader)`
  align-items: center;
  margin-top: ${spacing.plusHalf};
  margin-bottom: 0;
`;

export const GoalDelete = styled(Delete16)`
  margin-right: ${spacing.half};
`;

export const GoalEdit = styled(Edit16)`
  margin-right: ${spacing.half};
`;

export const ConditionsContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ModalConditionsText = styled.p`
  margin-left: ${spacing.half};
`;

export const PredictiveGoalsSpinner = styled(Spinner)`
  & {
    height: unset;
  }
`;

export const InProgressSpinningLogo = styled(PredictiveGoalsSpinner)`
  display: flex;
  align-items: flex-start;
`;

export const Body2InheritStyle = styled(Body2)`
  color: inherit;
`;

export const NoticeBannerBullet = styled.li`
  list-style-type: disc;
`;

export const SpacedWarningBanner = styled(WarningBanner)`
  ${bannerMarginOverride};
`;

export const SpacedErrorBanner = styled(ErrorBanner)`
  ${bannerMarginOverride};
`;

export const GoalBuilderTitle = styled.div`
  ${subtitle1};
  margin-bottom: ${spacing.plusHalf};
`;

export const GoalBuilderTitleBody = styled.p`
  ${body1};
  color: ${colors.textSecondary};
`;

export const GoalBuilderTitleDivider = styled(Divider)`
  margin: 0;
`;

export const GoalBuilderContainer = styled.div`
  background: ${colors.grey1};
  border-radius: ${spacing.half} ${spacing.half} 0 0;
`;

export const UpdatedGoalBuilderContainer = styled.div`
  background: ${colors.grey1};
  border-radius: ${spacing.half};
`;

export const GoalBuilderTitleContainer = styled.div`
  padding: ${spacing.plusHalf} ${spacing.plusHalf} 0 ${spacing.plusHalf};
`;

export const GoalBuilderTitlePreviewContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${spacing.base};
`;

/** Index Page */
export const IndexContentLayout = styled(MainContentLayout)`
  margin-top: ${spacing.x3};
  margin-bottom: ${spacing.x4};
  height: 100vh;
`;

export const InfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.double};
`;

export const InfoTextContainer = styled.div`
  max-width: ${pxToRem(640)};
`;

const mobileBreakpoint = pxToRem(768);

export const InfoIllustrationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (width <= 768px) {
    display: none;
  }
`;

export const PredictiveGoalsAlignedPageHeader = styled(PageHeader)`
  margin-top: ${spacing.double};
  margin-bottom: ${spacing.double};

  & > div > div > div {
    height: 100%;
  }
  & > div > div >  div > div {
    height: 100%;
  }
`;

export const PredictiveGoalsTableCell = styled(MarginLessBody2)`
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  vertical-align: middle;
`;

export const StyledDisabledText = styled.div`
  color: ${colors.textDisabled};
`;

export const PredictiveStrengthTableCell = styled(PredictiveGoalsTableCell)`
  & > div {
    display: flex;
    align-items: center;
  }
`;

export const PredictiveGoalsLastModifiedTableCell = styled(PredictiveGoalsTableCell)`
  min-width: ${pxToRem(220)};
`;

export const PredictiveGoalCaption = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

export const StyledUserProfileFieldCaption = styled(PredictiveGoalCaption)`
  padding-top: ${spacing.half};
`;

export const HeaderBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const PredictiveGoalsHeaderTitle = styled.h3`
  ${heading3};
  margin: 0;
`;

export const PredictiveGoalsSpacedCard = styled(HoverCard)`
  margin-left: 0;
  margin-right: 0;
  margin-bottom: ${spacing.double};
`;

export const PredictiveGoalTableHeaderContainer = styled(TableHeader)`
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};

  & > div {
    padding: ${pxToRem(14)};
  }

  & > div div[data-test='header-cell'] {
    padding: 0;
  }

  & > div > div {
    max-height: ${pxToRem(20)};
  }

  & path {
    fill: ${colors.textSecondary};
  }

  & span[data-test='tooltip-trigger'] {
    path:nth-of-type(odd) {
      fill: inherit;
    }
  }

  & > div:first-of-type {
    border-top-left-radius: ${pxToRem(8)};
    padding-left: ${spacing.plusThreeQuarter};
  }
  & > div:nth-last-of-type(2) {
    padding-right: 0;
  }
  & > div:last-of-type {
    border-top-right-radius: ${pxToRem(8)};
    padding-right: ${spacing.plusThreeQuarter};
  }
`;

export const PredictiveGoalTableRowContainer = styled(TableRows)`
  & > * {
    padding: ${spacing.threeQuarter} ${spacing.base};
  }

  & > div {
    > code {
      word-break: break-word;
      display: inline-block;
    }
  }

  & > *:first-of-type {
    padding-left: ${spacing.double};
  }
  & > *:last-of-type {
    padding-right: ${spacing.double};
    padding-left: 0;
    float: right;
  }
`;

export const PredictiveGoalSpacedShadowContainer = styled(Table)`
  border-radius: ${pxToRem(8)};
  ${smallShadow};

  & > div:last-child > div:first-of-type {
    border-bottom-left-radius: ${pxToRem(8)};
  }
  & > div:last-child > div:last-of-type {
    border-bottom-right-radius: ${pxToRem(8)};
  }
`;

export const GoalColumnHeaderColorUp = styled(UpArrow)`
  path {
    fill: ${colors.textLink};
  }
`;

export const GoalColumnHeaderColorDown = styled(DownArrow)`
  path {
    fill: ${colors.textLink};
  }
`;

export const GoalColumnHoverIconButton = styled.span`
  display: inline-flex;
  align-items: center;
`;

export const GoalColumnLeftAlignedHeaderCell = styled(TableHeaderCell)`
  padding-left: 0;
  padding-right: ${spacing.quarter};

  &[data-is-active-sort='true'] {
    color: ${colors.brandPurple};
  }

  &[data-is-active-sort='false'] {
    color: ${colors.textPrimary};
  }
`;

export const PreventNewGoalTrigger = styled.div`
  cursor: not-allowed;
`;

/** Edit Page */
export const SaveButtonContainer = styled.div`
  text-align: right;
  margin-top: ${spacing.base};
  margin-bottom: ${spacing.x3};
`;

export const EditInfoBanner = styled(InfoBanner)`
  ${bannerMarginOverride};
`;

export const SpacedFirstBanner = styled.div`
  & > div:first-of-type {
    margin-top: ${spacing.x3};
  }
`;

/** Status Page */
export const StyledLogo = styled(Logo)`
  height: ${spacing.double};
  width: ${spacing.double};
`;

export const StatusTextContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const StatusParagraph = styled.div`
  padding-top: ${spacing.base};
  ${body1};
  width: ${spacing.x48};
`;

export const StyledArrowRightCircle = styled(ArrowRightCircle)`
  margin-right: ${spacing.threeQuarter};
  margin-top: ${spacing.quarter};
`;

/** Results Page */
export const ResultsContainer = styled.div`
  justify-content: space-between;
  height: 100vh;
`;

export const SpacedHeaderText = styled.div`
  margin-bottom: ${spacing.double};
  display: flex;
  align-items: center;
`;

export const SpacedAiBadgeWrapper = styled.div`
  padding-left: ${spacing.quarter};
  display: flex;
  align-items: center;
`;

export const GoalCriteriaDivider = styled(Divider)`
  /* Empty styled component */
`;

export const StyledCaption = styled.div`
  ${caption};
  color: ${colors.grey60};
  display: flex;
  justify-content: flex-end;
  padding-top: ${spacing.threeQuarter};
  align-items: center;
  gap: ${spacing.threeEights};
`;

/** Deleting Page */
export const DeletingStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

/** Create Page */
export const LimitExceededLayout = styled(MainContentLayout)`
  margin-top: ${spacing.x3};
  margin-bottom: ${spacing.x6};
`;

export const LimitExceededSpacedImage = styled(LimitExceeded)`
  margin-bottom: ${spacing.threeQuarter};
`;

export const LimitExceededTitle = styled(Title)`
  margin-bottom: ${spacing.half};
`;

export const LimitExceededContainer = styled.div`
  background: ${colors.grey1};
  text-align: center;
  padding: ${spacing.x3} ${spacing.x24};
  border-radius: ${spacing.half};
`;

export const LimitExceededBody = styled.div`
  ${body1};
  margin-bottom: ${spacing.plusHalf};
`;

export const LimitExceededBack = styled(LinkButton)`
  display: inline-block;
`;

export const ContentUnavailableWrapper = styled.div`
  margin-bottom: ${spacing.x3};
`;

/** XAI - Left Card */
export const XaiLeftCard = styled.div`
  background: ${colors.white};
  box-shadow: 0 1px 1px rgb(0 0 0 / 0.06), 0 0 2px rgb(0 0 0 / 0.06), 0 0 1px rgb(0 0 0 / 0.04);
  border-radius: ${spacing.half};
  width: ${pxToRem(212)};
`;

export const XaiLeftCardHeader = styled.p`
  ${body2Bold};
  margin: 0;
  padding: ${spacing.threeQuarter} ${spacing.base};
  box-shadow: 0 ${spacing.single} ${spacing.single} rgb(0 0 0 / 0.06), 0 0 ${spacing.eighth} rgb(0 0 0 / 0.06), 0 0 ${spacing.single} rgb(0 0 0 / 0.04);
  border-radius: ${spacing.half} ${spacing.half} 0 0;
  background: linear-gradient(
    242.03deg,
    #ff4d80 6.53%,
    #fe4d81 11.5%,
    #fc4e82 16.47%,
    #f95086 21.44%,
    #f4528a 26.41%,
    #ee5490 31.38%,
    #e65897 36.35%,
    #de5b9f 41.31%,
    #d65fa7 46.28%,
    #ce62af 51.25%,
    #c666b6 56.22%,
    #c068bc 61.19%,
    #bb6ac0 66.16%,
    #b86cc4 71.13%,
    #b66dc5 76.1%,
    #b56dc6 81.07%
  );
  background-clip: text;
  -webkit-text-fill-color: transparent;
  /* stylelint-disable */
  text-fill-color: transparent;
`;
export const XaiLeftCardContent = styled.div`
  padding: ${spacing.base};
`;

export const BoldInLineText = styled.span`
  font-weight: ${fontWeights.bold};
`;

export const XaiLeftCardContentExploreButton = styled(SecondaryButton)`
  width: 100%;
  padding: ${spacing.base};
`;

export const XaiTotalCounts = styled(MarginLessBody2)`
  padding-bottom: ${spacing.base};
`;

export const XaiContributingFeatures = styled.div`
  ${body2};
  padding-bottom: ${spacing.plusHalf};
`;

export const XaiMostMeaningfulHeaderText = styled.div`
  ${subtitle2};
  display: flex;
  align-items: center;
  padding-bottom: ${spacing.threeEights};
  gap: ${spacing.quarter};
`;

export const XaiMonospaceText = styled.span`
  font-family: ${fonts.monospaced};
  font-size: ${fontSizes.s12};
  line-height: ${fontSizes.s20};
  color: ${colors.secondaryText};
`;

export const XaiMonospacedTextWithTooltip = styled(XaiMonospaceText)`
  text-align: left;
  font-size: ${fontSizes.s13};
  color: ${colors.textPrimary};
`;

export const StyledLeftNavTooltipWrapper = styled.div`
  width: 100%;
  > span {
    width: 100%;
  }
`;

export const StyledSlideOverTooltipWrapper = styled(StyledLeftNavTooltipWrapper)`
  max-width: ${pxToRem(180)};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const StyledSlideOverGraphFeatureText = styled(XaiMonospaceText)`
  padding-left: ${spacing.half};
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s13};
`;

export const XaiLeftNavTopFeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const XaiMonospaceFeatureText = styled(XaiMonospaceText)`
  font-size: ${fontSizes.s13};
  color: ${colors.textPrimary};
  padding-right: ${spacing.threeQuarter};
`;

export const XaiTopFeaturesEmptyStateText = styled.div`
  font-family: ${fonts.sansSerif};
  font-size: ${fontSizes.s14};
  font-weight: ${fontWeights.regular};
  line-height: ${fontSizes.s20};
  color: ${colors.secondaryText};
  padding-bottom: ${spacing.base};
`;

export const XaiContentWrapper = styled.div`
  margin-bottom: ${spacing.x6};
`;

export const XaiDrawerTile = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.grey5};
  border-radius: ${spacing.half};
  padding: ${spacing.base};
  ${subtleShadow};
`;

export const XaiSmallTile = styled(XaiDrawerTile)`
  ${body2};
  padding: ${spacing.base};
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr;
  gap: ${spacing.quarter} 0;
`;

export const XaiInnerTilesWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${spacing.threeQuarter};
`;

export const TileHeaderText = styled(MarginLessBody2)`
  color: ${colors.textSecondary};
`;

export const TileBodyText = styled.h4`
  color: ${colors.textPrimary};
  ${heading4};
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${spacing.quarter};
  margin: 0;
`;

export const DirectionalityFeatureTypeText = styled(MarginLessBody2)`
  color: ${colors.textSecondary};
  display: inline-flex;
  justify-content: flex-end;
  text-align: right;
  white-space: nowrap;
`;

export const XaiSlideOverTextWrapper = styled(DirectionalityFeatureTypeText)`
  padding-top: ${spacing.base};
`;

export const XaiLikelyTile = styled(XaiDrawerTile)`
  padding: 0px;
  margin-top: ${spacing.half};
  ${body2};
`;

export const XAILikelyTileContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
export const XAILikelyTileContentText = styled.span`
  padding-left: ${spacing.quarter};
`;

export const XAILikelyTileToggletipTrigger = styled.div`
  margin-left: auto;
`;

export const XaiLikelyBoldText = styled.span`
  font-weight: ${fontWeights.semiBold};
`;

export const XaiLikelyHeader = styled.div`
  display: flex;
  align-items: baseline;
  color: ${colors.textSecondary};
  padding: ${spacing.base};
`;

export const XaiSlideOverFooterText = styled.div`
  ${caption};
  color: ${colors.secondaryText};
  padding-top: ${spacing.threeQuarter};
  padding-bottom: ${spacing.threeEights};
`;

export const XaiSlideOverFooterItem = styled.a`
  ${caption};
  color: ${colors.secondaryText};
  text-decoration: underline;
`;

export const StyledMinusIcon = styled.div`
  display: flex;
  align-items: center;
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  > div:nth-of-type(even) {
    background-color: ${colors.grey1};
  }
`;

export const FeatureListItem = styled.div`
  padding: ${spacing.half} ${spacing.base} ${spacing.half} ${spacing.base};
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

export const XaiLikelyEmptyStateText = styled(MarginLessBody2)`
  font-size: ${fontSizes.s14};
  font-weight: ${fontWeights.regular};
  line-height: ${fontSizes.s20};
  color: ${colors.grey50};
  padding-bottom: ${spacing.base};
  align-items: center;
  text-align: center;
`;

export const XAILinkcaption = styled(Caption)`
  display: inline-block;
  color: ${colors.textSecondary};
  margin-top: ${spacing.plusHalf};
`;

export const StyledNewTabLink = styled(NewTabLink)`
  font-size: ${fontSizes.s12};
  color: ${colors.textSecondary};
`;

export const StyledPrGTemplateCardWrapper = styled.div`
  width: 100%;
`;

export const StyledPrGTemplateCard = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${spacing.threeEights};
  ${smallShadow};
  padding: ${spacing.base};
  gap: ${spacing.base};
  background: ${colors.white};
  height: ${pxToRem(124)};
  cursor: pointer;

  &:hover {
    box-shadow: ${largeShadow.boxShadow};
    transition: box-shadow ${duration.dramatic} ${easing.standard};
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const StyledTemplateIllustrationWrapper = styled.div`
  height: 100%;
  width: 100%;
  max-width: ${pxToRem(96)};
  max-height: ${pxToRem(96)};
  > svg {
    height: 100%;
    width: 100%;
  }
  border-radius: ${spacing.threeEights};
`;

export const StyledLikelyToChurn = styled(StyledTemplateIllustrationWrapper)`
  background-color: ${colors.purple10};
`;
export const StyledLikelyToPurchase = styled(StyledTemplateIllustrationWrapper)`
  background-color: ${colors.cyan10};
`;
export const StyledLikelyToSubscribe = styled(StyledTemplateIllustrationWrapper)`
  background-color: ${colors.mustard10};
`;

export const StyledTemplateCardTitle = styled.div`
  ${subtitle1};
  padding-bottom: ${spacing.threeEights};
`;

export const StyledTemplateCardDetails = styled.div`
  ${body2};
  color: ${colors.textSecondary};
`;

export const StyledPredictiveGoalsTemplateSelectionWrapper = styled.div`
  background-color: ${colors.grey5};
  border-radius: ${spacing.half};
  padding: ${spacing.plusHalf};
  margin-bottom: ${spacing.double};
`;

export const StyledPredictiveGoalsTemplateSelectionItems = styled.div`
  display: flex;
  gap: ${spacing.base};
  justify-content: space-between;
`;

export const TemplateHeader = styled.div`
  ${heading5};
  margin-bottom: ${spacing.threeQuarter};
`;

export const BorderRadiusSkeleton = styled(Skeleton)`
  border-radius: ${borderRadius.lg};
`;
